import {MithrilComponent} from '@bitstillery/mithril'
import {classes} from '@bitstillery/common/lib/utils'
import {state} from '@bitstillery/mithril'
import {clear_filter, place_filter} from '@bitstillery/common/lib/filters'
import {Button} from '@bitstillery/common/components'
import {$t, notifier} from '@bitstillery/common/app'
import m from '@bitstillery/mithril'

import type {CollectionProxy} from '@bitstillery/common/lib/collection'
import type {Filter} from '@bitstillery/common/types'

interface CollectionSelectionAttrs {
    className?: string
    collection: CollectionProxy
    disabled?: boolean
    filter: Filter
    on_submit: () => void | Promise<void>
}

/**
 * This component is meant to provide selection stats next to a
 * CollectionItems view, so it is clear how many selected items there
 * are in that CollectionItems view. Also provide a filter for non-selected
 * Items, where used.
 */
export class CollectionSelection extends MithrilComponent<CollectionSelectionAttrs> {
    data = state(
        {
            loading: false,
            toggle: false,
        },
        'collection-selection-data',
    )

    view(vnode: m.Vnode<CollectionSelectionAttrs>) {
        const collection = vnode.attrs.collection
        const selection = collection.state.selection
        return (
            <div className={classes('c-collection-selection', vnode.attrs.className)}>
                <div className='filter-wrapper'>
                    {place_filter(vnode.attrs.collection, vnode.attrs.filter, vnode.attrs.filter.title)}
                </div>
                <div className='selection-wrapper'>
                    <Button
                        active={collection.state.selection.mode === 'select'}
                        disabled={!collection.state.selection.mode && collection.state.loading}
                        icon='clipboard-plus-outline'
                        onclick={() => {
                            selection.all = false
                            selection.ids.splice(0, selection.ids.length)
                            if (collection.state.selection.mode === 'select') {
                                collection.state.selection.mode = ''
                            } else {
                                collection.state.selection.mode = 'select'
                            }
                        }}
                        text={$t('collection.include_label')}
                        tip={() => {
                            return $t('collection.include_tip', {count: selection.current.length})
                        }}
                        type='success'
                        variant='context'
                    />
                    <Button
                        active={selection.mode === 'deselect'}
                        disabled={!selection.current.length || (!collection.state.selection.mode && collection.state.loading)}
                        icon='clipboard-minus-outline'
                        onclick={() => {
                            selection.all = false
                            selection.ids.splice(0, selection.ids.length)
                            if (collection.state.selection.mode === 'deselect') {
                                collection.state.selection.mode = ''
                            } else {
                                collection.state.selection.mode = 'deselect'
                            }
                        }}
                        text={$t('collection.exclude_label')}
                        tip={() => {
                            return $t('collection.exclude_tip', {count: selection.current.length})
                        }}
                        type='warning'
                        variant='context'
                    />
                </div>

                <div className='action-wrapper mt-1'>
                    <Button
                        className='btn-submit'
                        disabled={(() => {
                            if (collection.state.loading) return true
                            if (this.data.loading || selection.mode === '') return true
                            return collection.selection_count() === 0
                        })()}
                        icon={(() => {
                            if (selection.mode === 'select') {
                                return 'clipboard-plus-outline'
                            } else if (selection.mode === 'deselect') {
                                return 'clipboard-minus-outline'
                            }
                            return 'edit'
                        })()}
                        loading={this.data.loading || collection.state.loading}
                        onclick={async () => {
                            this.data.loading = true
                            // Submit selection and requery afterwards to update the items.
                            await vnode.attrs.on_submit()
                            await Promise.all([collection.reset_query(), collection.transforms.selection()])

                            if (selection.mode === 'select') {
                                notifier.notify('Items were included successfully', 'info')
                            } else {
                                notifier.notify('Items were excluded successfully', 'info')
                            }
                            selection.ids.splice(0, selection.ids.length)
                            // The select/deselect mode has nothing more to do; exit mode.
                            if (
                                (selection.mode === 'select' && collection.state.items.length === 0) ||
                                (selection.mode === 'deselect' && selection.current.length === 0)
                            ) {
                                collection.state.selection.mode = ''
                                clear_filter(vnode.attrs.filter)
                            }

                            this.data.loading = false
                        }}
                        text={(() => {
                            const count = collection.selection_count()
                            // An unknown count (all rows, no total) gets the label without a number.
                            if (count === null) {
                                return $t('collection.modify_selection')
                            }
                            if (selection.mode === 'select') {
                                return $t('collection.include', {count})
                            } else if (selection.mode === 'deselect') {
                                return $t('collection.exclude', {count})
                            }
                            return $t('collection.modify_selection')
                        })()}
                        type={(() => {
                            if (selection.mode === 'select') {
                                return 'success'
                            } else if (selection.mode === 'deselect') {
                                return 'warning'
                            }
                            return 'default'
                        })()}
                    />
                </div>
            </div>
        )
    }
}
